import React, { CSSProperties, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useItemsControlSelector } from "userful-chronos-common-store/dist/itemsControl";

import "./Flyzone.scss";

interface IProps {
    children: React.ReactNode;
}

export default function Flyzone(props: IProps) {
    const { primaryId, fly, pointer, pointerOffset, pointerRatio, initialSize, useInitialSize } =
        useItemsControlSelector((state) => state.itemsControl.drag);
    const contentRef = useRef<HTMLDivElement | null>(null);
    const [measuredSize, setMeasuredSize] = useState<{ width: number; height: number } | null>(null);

    useLayoutEffect(() => {
        if (!fly) {
            setMeasuredSize(null);
            return;
        }
        if (!contentRef.current) {
            return;
        }
        const { offsetWidth, offsetHeight } = contentRef.current;
        setMeasuredSize({ width: offsetWidth, height: offsetHeight });
    }, [fly]);

    const computedOffset = useMemo(() => {
        if (!pointer || !pointerOffset) {
            return null;
        }

        let translateX = pointer.x - pointerOffset.x;
        let translateY = pointer.y - pointerOffset.y;

        if (!useInitialSize && pointerRatio && measuredSize?.width && measuredSize?.height) {
            translateX = pointer.x - pointerRatio.x * measuredSize.width;
            translateY = pointer.y - pointerRatio.y * measuredSize.height;
        }

        return { translateX, translateY };
    }, [measuredSize, pointer, pointerOffset, pointerRatio, useInitialSize]);

    const overlayStyle = useMemo(() => {
        if (!pointer || !computedOffset) {
            return null;
        }

        const translateX = computedOffset.translateX;
        const translateY = computedOffset.translateY;
        const style: CSSProperties = {
            transform: `translate(${translateX}px, ${translateY}px)`,
        };
        const width = useInitialSize ? initialSize?.width : measuredSize?.width;
        const height = useInitialSize ? initialSize?.height : measuredSize?.height;

        if (width) {
            style.width = width;
        }
        if (height) {
            style.height = height;
        }
        (style as any)["--stack-depth"] = 1;
        return style;
    }, [computedOffset, initialSize, measuredSize, pointer, useInitialSize]);

    const overlay = useMemo(() => {
        if (typeof document === "undefined") {
            return null;
        }
        if (!primaryId || !fly || !overlayStyle) {
            return null;
        }
        return createPortal(
            <div className="flyzone-overlay">
                <div className="flyzone-overlay-item" style={overlayStyle}>
                    <div className="flyzone-overlay-content" ref={contentRef}>
                        {fly}
                    </div>
                </div>
            </div>,
            document.body,
        );
    }, [fly, overlayStyle, primaryId]);

    return (
        <>
            {props.children}
            {overlay}
        </>
    );
}
